/**
 * useFieldValues
 *
 * Backs the New Report wizard's Filter "Value" picker. Hits
 * `GET /il/analytics/:tenant/:site/field-values?field=<field>` and returns
 * the most-frequent distinct values for that field over a 30-day lookback.
 *
 * Three reasons it can't just live in `useReportDslMetadata`:
 *   • The list is per-field, not per-suite — fetching every field's
 *     values up-front would scan ClickHouse N times for N fields.
 *   • The user only wants this data when they're actively editing a
 *     filter, so we lazy-load on demand.
 *   • Custom-dimension values change as new events stream in (way more
 *     dynamic than the catalog of dim/metric *keys*), so we want a
 *     dedicated cache window per field.
 *
 * The hook is a no-op when `field` is empty / `*` / `null` — useful so
 * callers don't have to guard themselves before mounting an Autocomplete.
 */

import { useCallback, useEffect, useState } from 'react';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';

export interface FieldValueBucket {
  value: string;
  count: number;
}

export interface FieldValuesPayload {
  field: string;
  lookbackDays: number;
  values: FieldValueBucket[];
  truncated: boolean;
}

interface CacheEntry {
  payload: FieldValuesPayload;
  fetchedAt: number;
}

// Per-(tenant, site path, field) module-level cache for the lifetime of the
// JS bundle. Server already caches for 5 minutes; this layer just avoids
// the network round-trip when the user reopens the same Filter row.
const moduleCache = new Map<string, CacheEntry>();

const cacheKey = (tenantId: string, siteId: string | undefined | null, field: string): string =>
  `${tenantId}::${infralytiqsAnalyticsSitePathSegment(siteId)}::${field}`;

export interface UseFieldValuesResult {
  values: FieldValueBucket[];
  /** True when the LIMIT was hit on the server — there are more values than were returned. */
  truncated: boolean;
  loading: boolean;
  error: string | null;
  /** Manual cache-busting refresh — useful from a refresh button if we ever add one. */
  refresh: () => Promise<void>;
}

/**
 * @param tenantId  resolved from `parentConfig.tenantId`
 * @param siteId    resolved from `parentConfig.siteId` (may be blank for tenant-wide CH config)
 * @param field     wire-format DSL field name (e.g. `event_type`,
 *                  `custom_dimensions.DAM`, `custom_metrics.time_spent`)
 *                  — pass `null` / `''` / `'*'` to leave the hook idle.
 * @param enabled   when `false`, skip fetching entirely (e.g. for ops
 *                  with no operand like `exists` / `not_exists`).
 */
export const useFieldValues = (
  tenantId: string | undefined,
  siteId: string | undefined | null,
  field: string | undefined | null,
  enabled: boolean = true,
): UseFieldValuesResult => {
  const [payload, setPayload] = useState<FieldValuesPayload | null>(() => {
    if (!enabled || !tenantId || !field || field === '*') return null;
    return moduleCache.get(cacheKey(tenantId, siteId, field))?.payload ?? null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchValues = useCallback(
    async (opts: { refresh?: boolean } = {}): Promise<void> => {
      if (!enabled || !tenantId || !field || field === '*') return;
      const pathSeg = infralytiqsAnalyticsSitePathSegment(siteId);
      const key = cacheKey(tenantId, siteId, field);
      if (!opts.refresh) {
        const hit = moduleCache.get(key);
        if (hit) {
          setPayload(hit.payload);
          return;
        }
      }
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({
          field,
          lookbackDays: '30',
          limit: '100',
        });
        if (opts.refresh) qs.set('refresh', '1');
        const res: any = await apiClient.get(
          `/il/analytics/${encodeURIComponent(tenantId)}/${encodeURIComponent(pathSeg)}/field-values?${qs.toString()}`,
        );
        // `apiClient.get()` returns the parsed JSON body unwrapped. The
        // server's `/field-values` endpoint replies with a flat
        // `{ success, values, truncated, ... }` envelope (no `data`
        // wrapper), but accept both shapes so a future server-side
        // refactor to a `{ status, message, data }` envelope doesn't
        // silently break the typeahead.
        const body: any = res?.data ?? res;
        const next: FieldValuesPayload = {
          field: body?.field ?? field,
          lookbackDays: Number(body?.lookbackDays ?? 30),
          values: Array.isArray(body?.values) ? body.values : [],
          truncated: !!body?.truncated,
        };
        moduleCache.set(key, { payload: next, fetchedAt: Date.now() });
        setPayload(next);
      } catch (err: any) {
        // Don't blow away a previously-cached payload — the autocomplete
        // is more useful with stale-but-non-empty options than empty.
        const msg = err?.response?.data?.error || err?.message || 'Failed to load field values';
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [tenantId, siteId, field, enabled],
  );

  useEffect(() => {
    if (!enabled || !tenantId || !field || field === '*') {
      setPayload(null);
      setError(null);
      return;
    }
    fetchValues();
  }, [fetchValues, enabled, tenantId, siteId, field]);

  return {
    values: payload?.values ?? [],
    truncated: payload?.truncated ?? false,
    loading,
    error,
    refresh: () => fetchValues({ refresh: true }),
  };
};
